import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CampaignIcon from '@mui/icons-material/Campaign';
import EventIcon from '@mui/icons-material/Event';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import useUser from '../../hooks/useUser';
import useFrontendUsage from '../../hooks/useFrontendUsage';
import {
  buildNewFrontendUrl,
  LEGACY_FRONTEND_BASE_URL,
  MANAGEMENT_DOMAIN_SWITCH_DATE,
  PUBLIC_PATHS,
  SHARED_MANAGEMENT_HOSTNAME,
} from '../../util/frontendMigration';
import { resolveViewTitle } from '../../util/viewTitle';

const INTRODUCTION_KEY = 'legacyFrontendSunset';
const ANNOUNCEMENT_KEY = 'newManagementFrontend';

// Hostname without the protocol, for display inside the dialog.
const LEGACY_HOSTNAME = LEGACY_FRONTEND_BASE_URL.replace(/^https?:\/\//, '');

const POINTS = [
  {
    Icon: EventIcon,
    title: `Ab dem ${MANAGEMENT_DOMAIN_SWITCH_DATE}: ${SHARED_MANAGEMENT_HOSTNAME} zeigt das neue Tool`,
    description:
      'Wer die gewohnte Adresse aufruft, landet ab diesem Tag automatisch im neuen Management. Login, Daten und Rechte bleiben dieselben.',
  },
  {
    Icon: BookmarkBorderIcon,
    title: `Diese Oberfläche bleibt unter ${LEGACY_HOSTNAME}`,
    description:
      'Sie wird nicht abgeschaltet. Wer sie weiter braucht, erreicht sie ab sofort dauerhaft unter dieser Adresse – am besten gleich als Lesezeichen anlegen.',
  },
  {
    Icon: CampaignIcon,
    title: 'Neue Funktionen gibt es nur noch im neuen Management',
    description:
      'Alles, was ab jetzt entwickelt wird, entsteht ausschließlich dort. In dieser Oberfläche kommt nichts Neues mehr dazu.',
  },
  {
    Icon: BuildCircleIcon,
    title: 'Wichtige Fehlerbehebungen kommen weiterhin auch hier an',
    description:
      'Damit der Betrieb sicher läuft, werden gravierende Fehler weiterhin in beiden Oberflächen behoben.',
  },
];

// One-time announcement of the management-domain switch-over and the feature
// freeze of this legacy frontend. Persisted per user via hasBeenIntroducedTo,
// like the theme and new-frontend introductions.
export default function LegacyFrontendSunsetModal() {
  const theme = useTheme();
  const location = useLocation();
  const auth = useAuth();
  const { updateUserPreferences } = useUser();
  const { trackSwitchToNewFrontend } = useFrontendUsage();
  const [saving, setSaving] = useState(false);

  // Snapshot of the older new-frontend announcement taken when the preferences
  // first arrive. Acknowledging that dialog must not make this one pop up in
  // the same breath, so a user who has just seen it gets this notice on the
  // next load instead.
  const [announcementSettled, setAnnouncementSettled] = useState(null);

  useEffect(() => {
    setAnnouncementSettled(null);
  }, [auth.token]);

  useEffect(() => {
    if (announcementSettled !== null) return;
    if (!auth.userPreferencesHydrated || !Array.isArray(auth.hasBeenIntroducedTo)) return;
    setAnnouncementSettled(auth.hasBeenIntroducedTo.includes(ANNOUNCEMENT_KEY));
  }, [announcementSettled, auth.userPreferencesHydrated, auth.hasBeenIntroducedTo]);

  const shouldShow = useMemo(
    () =>
      Boolean(
        auth.token &&
          auth.userPreferencesHydrated &&
          !auth.isCounterDevice &&
          announcementSettled === true &&
          !PUBLIC_PATHS.has(location.pathname) &&
          Array.isArray(auth.hasBeenIntroducedTo) &&
          auth.hasBeenIntroducedTo.includes('themeColorMode') &&
          !auth.hasBeenIntroducedTo.includes(INTRODUCTION_KEY)
      ),
    [
      auth.token,
      auth.userPreferencesHydrated,
      auth.isCounterDevice,
      auth.hasBeenIntroducedTo,
      announcementSettled,
      location.pathname,
    ]
  );

  // Persists the "seen" flag for this user and mirrors it into the auth state
  // so the dialog closes right away.
  const markSeen = async () => {
    const updated = await updateUserPreferences(null, {
      acknowledgeIntroductions: [INTRODUCTION_KEY],
    });
    if (auth.setHasBeenIntroducedTo) {
      auth.setHasBeenIntroducedTo(prev => {
        const fromServer = Array.isArray(updated?.hasBeenIntroducedTo)
          ? updated.hasBeenIntroducedTo
          : [];
        return [...new Set([...(prev || []), ...fromServer, INTRODUCTION_KEY])];
      });
    }
  };

  const handleAcknowledge = async () => {
    setSaving(true);
    try {
      await markSeen();
    } catch (_e) {
      toast.error('Speichern der Einstellungen fehlgeschlagen');
      setSaving(false);
    }
  };

  const handleSwitchNow = async () => {
    setSaving(true);
    try {
      await markSeen();
      const viewTitle = resolveViewTitle({
        pathname: location.pathname,
        menuItems: auth.menuItems,
      });
      try {
        await trackSwitchToNewFrontend({
          path: location.pathname,
          viewTitle: viewTitle || undefined,
          origin: 'sunset-dialog',
        });
      } catch (_e) {
        // Tracking must never block the switch.
      }
      window.location.assign(buildNewFrontendUrl(location.pathname));
    } catch (_e) {
      toast.error('Speichern der Einstellungen fehlgeschlagen');
      setSaving(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <Dialog
      open={shouldShow}
      onClose={() => {}}
      fullWidth
      maxWidth='sm'
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: alpha('#000000', 0.52),
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          backgroundImage: t =>
            t.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${theme.palette.background.paper} 42%)`
              : `linear-gradient(145deg, ${alpha(theme.palette.primary.light, 0.45)} 0%, ${theme.palette.background.paper} 40%)`,
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.22 : 0.35
              ),
            }}
          >
            <CampaignIcon sx={{ color: 'text.primary' }} />
          </Box>
          <Typography variant='h5' component='h1' sx={{ fontWeight: 700 }}>
            {SHARED_MANAGEMENT_HOSTNAME} wechselt zum neuen Management
          </Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5, maxWidth: 500 }}>
          Das neue Management-Tool übernimmt. Damit du nicht überrascht wirst, hier in Kürze, was
          sich ändert – und was gleich bleibt.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        <Stack spacing={1.5}>
          {POINTS.map(({ Icon, title, description }) => (
            <Stack key={title} direction='row' spacing={1.5} alignItems='flex-start'>
              <Icon sx={{ mt: 0.25, fontSize: 22, color: 'text.secondary' }} />
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {description}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
        <Box
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.03),
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            Adresse dieser Oberfläche ab sofort
          </Typography>
          <Link
            href={LEGACY_FRONTEND_BASE_URL}
            target='_blank'
            rel='noopener noreferrer'
            variant='body2'
            sx={{ fontWeight: 600, wordBreak: 'break-all' }}
          >
            {LEGACY_HOSTNAME}
          </Link>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 3, pt: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
      >
        <Button
          variant='contained'
          color='primary'
          size='large'
          onClick={handleSwitchNow}
          disabled={saving}
          startIcon={<AutoAwesomeIcon />}
          sx={{ width: '100%', px: 3, py: 1.25, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? 'Einen Moment…' : 'Jetzt zum neuen Management wechseln'}
        </Button>
        <Box sx={{ minHeight: 32, display: 'flex', justifyContent: 'center' }}>
          <Button
            color='inherit'
            size='small'
            onClick={handleAcknowledge}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 400,
              color: 'text.disabled',
              '&:hover': { color: 'text.secondary', backgroundColor: 'transparent' },
            }}
          >
            Verstanden – ich bleibe erstmal hier
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
